<template>
  <b-card
    no-body
    :class="['bookmark-card', { 'bookmark-card--list': isListView }]"
  >
    <template v-if="isListView">
      <div class="list-shell">
        <div class="list-top">
          <div class="chip-row">
            <span v-if="contextLabel" class="context-chip context-chip--list">
              {{ contextLabel }}
            </span>
            <span
              v-if="shouldShowRetiredTag"
              class="context-chip context-chip--retired"
            >
              Retired
            </span>
          </div>

          <b-button
            v-if="allowBookmark"
            class="bookmark-btn bookmark-btn--list"
            :variant="isBookmarked ? 'light-success' : 'outline-secondary'"
            size="sm"
            :disabled="bookmarkLoading"
            @click.stop="toggleBookmark"
          >
            <b-spinner v-if="bookmarkLoading" small />
            <template v-else>
              <span class="mr-50">{{
                isBookmarked ? 'Saved' : 'Bookmark'
              }}</span>
              <svg
                v-if="isBookmarked"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M6 3a2 2 0 0 0-2 2v16l8-3.5 8 3.5V5a2 2 0 0 0-2-2H6Z"
                />
              </svg>
              <svg
                v-else
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M6 3a2 2 0 0 0-2 2v16l8-3.5 8 3.5V5a2 2 0 0 0-2-2H6Z"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </template>
          </b-button>
        </div>

        <div class="list-body" role="button" @click="openDetail">
          <div class="list-content">
            <div class="hero-thumb list-thumb" :style="thumbnailStyle">
              <img
                v-if="photoUrl"
                class="product-photo"
                :src="photoUrl"
                :alt="item.PRODUCT"
              />
              <img
                v-else-if="categoryIcon"
                class="category-icon"
                :src="categoryIcon"
                :alt="categoryLabel"
              />
              <span v-else class="initials">{{ initials }}</span>
            </div>

            <div class="list-content-body">
              <h5 class="list-title" :title="item.PRODUCT">
                {{ item.PRODUCT }}
              </h5>
              <div class="list-meta text-muted">
                <span v-if="formattedDate" class="meta-item">
                  <span class="meta-value">Updated {{ formattedDate }}</span>
                </span>
              </div>
            </div>
          </div>
          <div class="list-desc" :title="descriptionText">
            {{ descriptionText || '-' }}
          </div>
        </div>

        <div class="list-footer">
          <div class="list-footer-left"></div>
          <div class="list-actions">
            <b-button
              variant="primary"
              size="sm"
              class="mb-1"
              @click="openDetail"
            >
              View Detail
            </b-button>
            <b-button
              v-if="hasChildren"
              variant="outline-secondary"
              size="sm"
              class="mb-1 children-link-btn"
              :aria-controls="childCollapseId"
              :aria-expanded="childrenOpen"
              @click.stop="childrenOpen = !childrenOpen"
            >
              {{ childrenOpen ? 'Hide Subproducts' : 'Show Subproducts' }}
            </b-button>
          </div>
        </div>
      </div>

      <b-collapse
        v-if="hasChildren"
        :id="childCollapseId"
        v-model="childrenOpen"
        class="list-children"
      >
        <b-list-group flush>
          <b-list-group-item
            v-for="childRow in flattenedChildren"
            :key="childRow.key"
            button
            @click="openChild(childRow.item)"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div
                class="w-100"
                :style="{ paddingLeft: `${childRow.depth * 16}px` }"
              >
                <strong>{{ childRow.item.PRODUCT }}</strong>
                <!-- <div class="small text-muted">
                  Last update {{ childFormattedDate(childRow.item) }}
                </div> -->
              </div>
              <feather-icon icon="ExternalLinkIcon" size="16" />
            </div>
          </b-list-group-item>
        </b-list-group>
      </b-collapse>
    </template>

    <template v-else>
      <div class="card-top">
        <div class="chip-row">
          <div v-if="contextLabel" class="context-chip">
            {{ contextLabel }}
          </div>
          <span
            v-if="shouldShowRetiredTag"
            class="context-chip context-chip--retired"
          >
            Retired
          </span>
        </div>
        <b-button
          v-if="allowBookmark"
          class="bookmark-btn"
          :variant="isBookmarked ? 'light-success' : 'outline-secondary'"
          size="sm"
          :disabled="bookmarkLoading"
          @click.stop="toggleBookmark"
        >
          <b-spinner v-if="bookmarkLoading" small />
          <template v-else>
            <span v-if="isBookmarked" class="mr-50">Saved</span>
            <span v-else class="mr-50">Bookmark</span>
            <svg
              v-if="isBookmarked"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M6 3a2 2 0 0 0-2 2v16l8-3.5 8 3.5V5a2 2 0 0 0-2-2H6Z" />
            </svg>
            <svg
              v-else
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M6 3a2 2 0 0 0-2 2v16l8-3.5 8 3.5V5a2 2 0 0 0-2-2H6Z"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </template>
        </b-button>
      </div>

      <div class="card-hero" role="button" @click="openDetail">
        <div class="hero-thumb" :style="thumbnailStyle">
          <img
            v-if="photoUrl"
            class="product-photo"
            :src="photoUrl"
            :alt="item.PRODUCT"
          />
          <img
            v-else-if="categoryIcon"
            class="category-icon"
            :src="categoryIcon"
            :alt="categoryLabel"
          />
          <span v-else class="initials">{{ initials }}</span>
        </div>
        <div class="hero-meta">
          <h5 class="title mb-25" :title="item.PRODUCT">{{ item.PRODUCT }}</h5>
          <div class="subtitle text-muted">
            <span v-if="formattedDate">• Updated {{ formattedDate }}</span>
          </div>
        </div>
      </div>

      <div class="card-content">
        <div class="description" v-html="descriptionPreview"></div>

        <div class="action-row">
          <div class="d-flex align-items-center flex-wrap">
            <b-button
              variant="primary"
              size="sm"
              class="mr-1 mb-1"
              @click="openDetail"
            >
              View Detail
            </b-button>
            <b-button
              v-if="hasChildren"
              variant="outline-secondary"
              size="sm"
              class="mb-1"
              :aria-controls="childCollapseId"
              :aria-expanded="childrenOpen"
              @click.stop="childrenOpen = !childrenOpen"
            >
              {{ childrenOpen ? 'Hide Subproducts' : 'Show Subproducts' }}
            </b-button>
          </div>
        </div>

        <b-collapse
          v-if="hasChildren"
          :id="childCollapseId"
          v-model="childrenOpen"
          class="mt-2"
        >
          <b-list-group flush>
            <b-list-group-item
              v-for="childRow in flattenedChildren"
              :key="childRow.key"
              button
              @click="openChild(childRow.item)"
            >
              <div class="d-flex justify-content-between align-items-center">
                <div
                  class="w-100"
                  :style="{ paddingLeft: `${childRow.depth * 16}px` }"
                >
                  <strong>{{ childRow.item.PRODUCT }}</strong>
                  <!-- <div class="small text-muted">
                    Last update {{ childFormattedDate(childRow.item) }}
                  </div> -->
                </div>
                <feather-icon icon="ExternalLinkIcon" size="16" />
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-collapse>
      </div>
    </template>
  </b-card>
</template>

<script>
import {
  BCard,
  BButton,
  BSpinner,
  BCollapse,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

const CATEGORY_ICONS = {
  communication: require('@/assets/images/products/unit/Icon - Communication.png'),
  datacomm: require('@/assets/images/products/unit/Icon - Datacomm.png'),
  internet: require('@/assets/images/products/unit/Icon - Internet.png'),
  wireless: require('@/assets/images/products/unit/Icon - Wireless.png'),
  'cyber security': require('@/assets/images/products/unit/CyberSecurity.png'),
}

const CATEGORY_ID_MAP = {
  1: 'communication',
  2: 'datacomm',
  3: 'internet',
  4: 'wireless',
  5: 'cyber security',
}

export default {
  name: 'ProductBookmarkCard',
  components: {
    BCard,
    BButton,
    BSpinner,
    BCollapse,
    BListGroup,
    BListGroupItem,
    FeatherIcon,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    context: {
      type: String,
      default: '',
    },
    viewMode: {
      type: String,
      default: 'grid',
    },
    allowBookmark: {
      type: Boolean,
      default: true,
    },
    showRetiredTag: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isBookmarked: !!this.item.is_bookmarked,
      bookmarkLoading: false,
      childrenOpen: false,
    }
  },
  computed: {
    isListView() {
      return this.viewMode === 'list'
    },
    contextLabel() {
      return this.context || this.$route?.meta?.breadcrumb?.[1]?.text || ''
    },
    flattenedChildren() {
      return this.flattenChildTree(this.item?.child_details || [])
    },
    childCount() {
      return this.flattenedChildren.length
    },
    hasChildren() {
      return this.childCount > 0
    },
    isRetired() {
      return Number(this.item?.RETIREMENT ?? this.item?.retirement) === 1
    },
    shouldShowRetiredTag() {
      return this.showRetiredTag && this.isRetired
    },
    initials() {
      if (!this.item?.PRODUCT) return ''
      const parts = this.item.PRODUCT.trim().split(/\s+/)
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
    },
    categoryKey() {
      const raw =
        this.item?.PROD_CATEGORY ??
        this.item?.product_category_details?.name ??
        this.item?.product_category ??
        this.item?.CATEGORY ??
        this.item?.category ??
        this.contextLabel
      if (!raw) return ''
      if (typeof raw === 'number') return CATEGORY_ID_MAP[raw] || ''
      const normalized = String(raw)
        .trim()
        .toLowerCase()
      if (CATEGORY_ICONS[normalized]) return normalized
      if (normalized.includes('datacomm')) return 'datacomm'
      if (normalized.includes('data') && normalized.includes('comm'))
        return 'datacomm'
      if (normalized.includes('communication')) return 'communication'
      if (normalized.includes('internet')) return 'internet'
      if (normalized.includes('wireless')) return 'wireless'
      if (normalized.includes('cyber')) return 'cyber security'
      if (CATEGORY_ID_MAP[Number(normalized)])
        return CATEGORY_ID_MAP[Number(normalized)]
      return ''
    },
    categoryIcon() {
      return CATEGORY_ICONS[this.categoryKey] || ''
    },
    categoryLabel() {
      if (!this.categoryKey) return ''
      return this.categoryKey.replace(/\b\w/g, ch => ch.toUpperCase())
    },
    photoUrl() {
      const list = this.item?.photo_product
      if (Array.isArray(list) && list.length) {
        const first = list[0] || {}
        return first.link || first.url || first.path || ''
      }
      if (typeof list === 'string') return list
      return ''
    },
    thumbnailStyle() {
      if (this.photoUrl) {
        return {
          backgroundColor: '#f8fafc',
          backgroundImage: 'none',
          boxShadow: '0 12px 32px rgba(8,15,52,0.18)',
        }
      }
      if (this.categoryIcon) {
        return {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          boxShadow: '0 12px 32px rgba(8,15,52,0.18)',
        }
      }
      const seed = String(this.item?.KODE || this.item?.PRODUCT || '')
      let h = 0
      for (let i = 0; i < seed.length; i++)
        h = (h * 31 + seed.charCodeAt(i)) % 360
      const h2 = (h + 36) % 360
      const colorA = `hsl(${h} 75% 55%)`
      const colorB = `hsl(${h2} 70% 45%)`
      return {
        backgroundImage: [
          `radial-gradient(circle at 16% 18%, rgba(255,255,255,0.18), transparent 35%)`,
          `radial-gradient(circle at 70% 0%, rgba(255,255,255,0.12), transparent 38%)`,
          `linear-gradient(135deg, ${colorA}, ${colorB})`,
        ].join(','),
        boxShadow: '0 12px 32px rgba(8,15,52,0.28)',
      }
    },
    formattedDate() {
      return this.formatDate(this.item?.LAST_UPDATE)
    },
    descriptionPreview() {
      const clean = this.sanitizeHtml(this.item?.SHORT_DESCRIPTION || '')
      return this.truncateHtml(clean, 220)
    },
    descriptionText() {
      const clean = this.sanitizeHtml(this.item?.SHORT_DESCRIPTION || '')
      const tmp = document.createElement('div')
      tmp.innerHTML = clean
      return (tmp.textContent || tmp.innerText || '').trim()
    },
    childCollapseId() {
      return `child-collapse-${this.item?.KODE ||
        Math.random()
          .toString(36)
          .slice(2)}`
    },
  },
  watch: {
    'item.is_bookmarked': function(val) {
      this.isBookmarked = !!val
    },
    'item.KODE': function() {
      this.childrenOpen = false
    },
  },
  mounted() {
    if (this.item.is_bookmarked === undefined) {
      this.loadLocalBookmark()
    }
  },
  methods: {
    sanitizeHtml(html = '') {
      if (!html) return ''
      try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const bad = doc.querySelectorAll('script,style,iframe,object,embed')
        bad.forEach(n => n.remove())
        const walk = node => {
          if (node.attributes) {
            Array.from(node.attributes).forEach(attr => {
              const name = attr.name.toLowerCase()
              const val = attr.value || ''
              if (name.startsWith('on')) node.removeAttribute(attr.name)
              if (
                name === 'href' &&
                val
                  .trim()
                  .toLowerCase()
                  .startsWith('javascript:')
              )
                node.removeAttribute('href')
              if (
                name === 'src' &&
                val
                  .trim()
                  .toLowerCase()
                  .startsWith('javascript:')
              )
                node.removeAttribute('src')
            })
          }
          node.childNodes &&
            node.childNodes.forEach(child => {
              if (child.nodeType === 1) walk(child)
            })
        }
        walk(doc.body)
        return doc.body.innerHTML
      } catch (e) {
        const tmp = document.createElement('div')
        tmp.innerHTML = html
        return tmp.textContent || tmp.innerText || ''
      }
    },
    truncateHtml(html = '', max = 220) {
      const tmp = document.createElement('div')
      tmp.innerHTML = html
      const text = tmp.textContent || tmp.innerText || ''
      const truncated = text.length > max ? `${text.slice(0, max)}...` : text
      return `<p>${this.escapeHtml(truncated)}</p>`
    },
    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    flattenChildTree(nodes, depth = 0, parentKey = 'root') {
      if (!Array.isArray(nodes) || !nodes.length) return []
      const rows = []
      nodes.forEach((node, index) => {
        if (!node) return
        const keyPart = String(node.KODE || node.PRODUCT || `${index}`)
        rows.push({
          key: `${parentKey}-${depth}-${keyPart}-${index}`,
          depth,
          item: node,
        })

        const nestedChildren =
          node.CHILD || node.child_details || node.childs || node.children || []
        if (Array.isArray(nestedChildren) && nestedChildren.length) {
          const nestedRows = this.flattenChildTree(
            nestedChildren,
            depth + 1,
            `${parentKey}-${keyPart}-${index}`
          )
          rows.push(...nestedRows)
        }
      })
      return rows
    },
    openDetail() {
      const route = this.detailTo(this.item?.KODE)
      if (route && this.$router) {
        this.$router.push(route).catch(() => {})
      }
    },
    openChild(child) {
      if (!child?.KODE) return
      const route = this.detailTo(child.KODE)
      if (route && this.$router) {
        this.$router.push(route).catch(() => {})
      }
    },
    formatDate(dateValue) {
      if (!dateValue) return ''
      try {
        if (this.$moment) {
          return this.$moment(dateValue).format('DD MMM YYYY')
        }
        const d = new Date(String(dateValue).replace(' ', 'T'))
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
      } catch (e) {
        return ''
      }
    },
    childFormattedDate(child) {
      const value =
        child?.LAST_UPDATE ??
        child?.last_update ??
        child?.UPDATED_AT ??
        child?.updated_at ??
        child?.MODIFIED_AT ??
        child?.modified_at
      return this.formatDate(value) || '-'
    },
    detailTo(id) {
      const label = (this.contextLabel || '').toLowerCase()
      if (label === 'bundling' || label === 'product bundling')
        return { name: 'product-bundling-detail', params: { key: id } }
      if (label === 'communication')
        return { name: 'product-communication-detail', params: { key: id } }
      if (label === 'datacomm')
        return { name: 'product-datacomm-detail', params: { key: id } }
      if (label === 'internet')
        return { name: 'product-internet-detail', params: { key: id } }
      if (label === 'wireless')
        return { name: 'product-wireless-detail', params: { key: id } }
      if (label === 'cyber security' || label === 'cyber-security')
        return { name: 'product-cyber-security-detail', params: { key: id } }
      if (label === 'fitur')
        return { name: 'product-fitur-detail', params: { key: id } }
      if (label === 'produk')
        return { name: 'product-produk-detail', params: { key: id } }
      if (label === 'varian')
        return { name: 'product-varian-detail', params: { key: id } }
      if (label === 'dtp' || label === 'digital touch point')
        return { name: 'product-dtp-detail', params: { key: id } }
      if (label === 'cons')
        return { name: 'product-cfu-cons-detail', params: { key: id } }
      if (label === 'ebis')
        return { name: 'product-cfu-ebis-detail', params: { key: id } }
      if (label === 'wibs')
        return { name: 'product-cfu-wibs-detail', params: { key: id } }
      return { name: 'product-detail', params: { key: id } }
    },
    loadLocalBookmark() {
      try {
        const raw = localStorage.getItem('product_bookmarks')
        const parsed = raw ? JSON.parse(raw) : []
        this.isBookmarked =
          Array.isArray(parsed) && parsed.includes(String(this.item?.KODE))
      } catch (e) {
        this.isBookmarked = !!this.item?.is_bookmarked
      }
    },
    persistBookmarkLocal(productKode, bookmarked) {
      try {
        const key = 'product_bookmarks'
        const raw = localStorage.getItem(key)
        let items = []
        if (raw) items = JSON.parse(raw)
        const kode = String(productKode)
        if (bookmarked) {
          if (!items.includes(kode)) items.push(kode)
        } else {
          items = items.filter(item => item !== kode)
        }
        localStorage.setItem(key, JSON.stringify(items))
      } catch (e) {
        // ignore persistence errors
      }
    },
    getUserKode() {
      return this.$store.getters['oauth/getActiveUser']?.id || ''
    },
    async toggleBookmark() {
      if (!this.allowBookmark || this.bookmarkLoading || !this.item?.KODE)
        return
      const userKode = this.getUserKode()
      if (!userKode) {
        this.$bvToast.toast('User data not found', {
          title: 'Bookmark',
          variant: 'danger',
          solid: true,
        })
        return
      }

      const willAdd = !this.isBookmarked
      const endpoint = willAdd
        ? `${process.env.VUE_APP_API_HELPER_URL}/product/bookmarks/add`
        : `${process.env.VUE_APP_API_HELPER_URL}/product/bookmarks/remove`

      this.bookmarkLoading = true
      try {
        await this.$http.put(endpoint, {
          user_kode: userKode,
          product_kode: [this.item.KODE],
        })
        this.isBookmarked = willAdd
        this.$set(this.item, 'is_bookmarked', willAdd)
        this.persistBookmarkLocal(this.item.KODE, willAdd)
        this.$bvToast.toast(
          willAdd ? 'Saved to bookmark' : 'Bookmark removed',
          {
            title: 'Bookmark',
            variant: willAdd ? 'success' : 'warning',
            solid: true,
          }
        )
        this.$emit('bookmark-change', {
          kode: this.item.KODE,
          bookmarked: willAdd,
        })
      } catch (err) {
        this.$bvToast.toast('Failed to update bookmark', {
          title: 'Bookmark',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.bookmarkLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.bookmark-card {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  padding: 1rem 1rem 0.75rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 360px;
}

.bookmark-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 60px rgba(15, 23, 42, 0.12);
}

.bookmark-card--list {
  min-height: 210px;
  padding: 1.15rem 1.25rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.05);
  background: #f8f9fb;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
}

.bookmark-card--list:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
}

.list-shell {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  height: 100%;
}

.list-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.chip-row {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.list-body {
  min-width: 0;
  cursor: pointer;
}

.list-content {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 0.85rem;
  align-items: center;
}

.list-content-body {
  min-width: 0;
}

.list-thumb {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
}

.list-thumb::after {
  border-radius: 16px;
}

.context-chip--list {
  width: fit-content;
  margin-bottom: 0;
  padding: 0.24rem 0.72rem;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.bookmark-btn--list {
  padding: 0.22rem 0.74rem;
  font-size: 0.76rem;
  font-weight: 700;
  min-height: 30px;
  line-height: 1;
}

.list-title {
  margin: 0;
  font-size: 2.1rem;
  line-height: 1.1;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.02em;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.list-desc {
  color: #2f3640;
  font-size: 1.02rem;
  line-height: 1.68;
  margin-top: 0.8rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  min-height: 3.36em;
}

.list-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.7rem;
  font-size: 0.95rem;
  line-height: 1.4;
  margin-top: 0.2rem;
}

.meta-item {
  display: inline-flex;
  align-items: center;
}

.meta-value {
  color: #a0a7b3;
  font-size: 0.95rem;
}

.list-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-top: auto;
}

.list-footer-left {
  display: flex;
  align-items: center;
  min-height: 32px;
}

.list-summary-tag {
  margin: 0;
}

.list-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.42rem;
}

.list-actions .btn {
  margin: 0 !important;
}

.children-link-btn {
  border-color: rgba(148, 163, 184, 0.45) !important;
  color: #475569 !important;
  background: #fff !important;
  font-weight: 600;
}

.children-link-btn:hover {
  color: #1f2937 !important;
  background: #f8fafc !important;
}

.list-children {
  margin-top: 0.2rem !important;
  padding-top: 0.55rem;
  border-top: 1px solid rgba(148, 163, 184, 0.24);
}

.list-children .list-group-item {
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  margin-bottom: 0.35rem;
}

.list-children .list-group-item:last-child {
  margin-bottom: 0;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.context-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  background: linear-gradient(120deg, #eef2ff, #e0f2fe);
  color: #0f172a;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.01em;
}

.context-chip--retired {
  background: rgba(239, 68, 68, 0.14);
  color: #b91c1c;
}

.bookmark-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 0.3rem 0.9rem;
  font-weight: 600;
}

.card-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0.35rem 0.1rem 0.6rem;
}

.hero-thumb {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  position: relative;
}

.hero-thumb::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.initials {
  font-weight: 800;
  font-size: 1.5rem;
  letter-spacing: 0.02em;
}

.category-icon {
  width: 44px;
  height: 44px;
  object-fit: contain;
}

.product-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
}

.hero-meta {
  flex: 1;
  min-width: 0;
}

.hero-meta .title {
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subtitle {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 0.86rem;
}

.card-content {
  background: #fff;
  border-radius: 14px;
  padding: 0.85rem 0.9rem;
  border: 1px solid rgba(15, 23, 42, 0.04);
  flex: 1;
  display: flex;
  flex-direction: column;
}

.description {
  color: #1f2937;
  font-size: 0.95rem;
  line-height: 1.5;
  min-height: 58px;
  flex: 1;
}

.pill-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 0.65rem 0 0.45rem;
}

.pill {
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.82rem;
  display: inline-flex;
  align-items: center;
}

.pill.accent {
  background: rgba(14, 165, 233, 0.14);
  color: #0369a1;
}

.pill.muted {
  background: rgba(15, 23, 42, 0.05);
  color: #475569;
}

.action-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: auto;
}

@media (max-width: 768px) {
  .hero-meta .title {
    white-space: normal;
  }
}

@media (max-width: 992px) {
  .list-title {
    font-size: 1.7rem;
  }

  .list-content {
    grid-template-columns: 64px minmax(0, 1fr);
    gap: 0.75rem;
  }

  .list-thumb {
    width: 64px;
    height: 64px;
  }

  .list-desc {
    font-size: 0.97rem;
  }
}

@media (max-width: 640px) {
  .bookmark-card--list {
    min-height: 186px;
    padding: 0.95rem 0.95rem 0.9rem;
  }

  .list-top {
    align-items: flex-start;
  }

  .list-title {
    font-size: 1.38rem;
    white-space: normal;
    line-height: 1.2;
  }

  .list-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .list-actions {
    justify-content: flex-start;
  }
}
</style>
